// File: src/hooks/useContent/articleHandling.js

import {useEffect, useRef} from 'react';
import { transformArticleHTML } from '../../utils/html/transformHtml';

/* ------------------------------------------------------------------ */
/*  Configuration – prefetching and retained reading history          */
/* ------------------------------------------------------------------ */
const PREFETCH_AHEAD = 5;   // how many articles **after** the current one to pre‑fetch
const STEP_SIZE      = 5;   // how many to grab at a time during endless‑scroll
export const ARTICLE_HISTORY_LIMIT = 15;

export function trimArticleHistory(cache, firstRetainedIndex) {
    const expired = Object.keys(cache).filter(index => Number(index) < firstRetainedIndex);
    if (!expired.length) return cache;
    const next = { ...cache };
    expired.forEach(index => delete next[index]);
    return next;
}

/**
 * useArticleHandling
 * --------------------------------------------------
 * Keeps every non‑API article workflow:
 *   • render‑HTML transform side‑effect
 *   • article selection & prefetch
 *   • endless‑scroll helpers
 *   • in‑view handler
 */
export const useArticleHandling = ({
                                       flatToc,
                                       rawArticles,
                                       articles,
                                       setArticles,
                                       setRawArticles,

                                       selectedRelease,
                                       tocData,
                                       offlineMode,

                                       currentArticleIndex,
                                       setCurrentArticleIndex,
                                       setSelectedArticleId,
                                       setSelectedParentId,

                                       fetchArticle,
                                       fetchArticlesBatch,

                                       isLoading,
                                   }) => {
    const nextRequestRef = useRef(null);
    const emptyRangeRef = useRef(null);
    const selectionRef = useRef(0);
    // Evict both representations: unmounting alone still retains the raw HTML
    // and transformed React trees. Older topics can be fetched by Load Previous.
    useEffect(() => {
        if (currentArticleIndex == null) return;
        const firstRetainedIndex = Math.max(0, currentArticleIndex - ARTICLE_HISTORY_LIMIT);
        setArticles(cache => trimArticleHistory(cache, firstRetainedIndex));
        setRawArticles(cache => trimArticleHistory(cache, firstRetainedIndex));
    }, [currentArticleIndex, setArticles, setRawArticles]);
    /* ------------------------------------------------------------------ */
    /*  Transform raw → rendered                                          */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        if (!flatToc.length) return;
        Object.entries(rawArticles).forEach(([idx, body]) => {
            if (articles[idx] !== undefined) return;
            const html = transformArticleHTML(
                body,
                selectedRelease,
                tocData,
                offlineMode
            );
            setArticles((a) => ({ ...a, [idx]: html }));
        });
    }, [
        rawArticles,
        flatToc,
        selectedRelease,
        tocData,
        offlineMode,
        articles,
        setArticles,
    ]);

    /* ------------------------------------------------------------------ */
    /*  Select and pre‑fetch                                              */
    /* ------------------------------------------------------------------ */
    const fetchArticleRange = async (flat, start, count, selection = selectionRef.current) => {
        const nodes = flat.slice(start, start + count);
        if (!nodes.length) return;
        let receivedCount = 0;

        await fetchArticlesBatch(nodes.map(node => node._id), (documents) => {
            if (selection !== selectionRef.current) return;
            // Batch responses contain documents in database order, not TOC order.
            const byId = new Map(documents.map(doc => [doc._id, doc]));
            const byTopicId = new Map(documents.map(doc => [doc.topic_id, doc]));
            const bodies = {};
            nodes.forEach((node, offset) => {
                const doc = byId.get(node._id) || byTopicId.get(node._id);
                if (doc) bodies[start + offset] = doc.body || '';
            });
            receivedCount = Object.keys(bodies).length;
            setRawArticles((a) => ({ ...a, ...bodies }));
        });
        return receivedCount;
    };

    const handleArticleSelect = async (articleId, flat = flatToc) => {
        const idx = flat.findIndex((i) => i._id === articleId);
        if (idx < 0) return;
        const selection = ++selectionRef.current;
        nextRequestRef.current = null;
        emptyRangeRef.current = null;
        setSelectedParentId(flat[idx].parent_id);
        setCurrentArticleIndex(idx);
        setSelectedArticleId(articleId);
        setArticles({});
        setRawArticles({});

        await fetchArticleRange(flat, idx, PREFETCH_AHEAD + 1, selection);
    };

    /* ------------------------------------------------------------------ */
    /*  Endless scroll helpers                                            */
    /* ------------------------------------------------------------------ */
    const loadNextArticle = async () => {
        if (isLoading || nextRequestRef.current || currentArticleIndex === null) return;
        const loaded = Object.keys(articles).map(Number);
        if (!loaded.length) return;
        // Wait for the previous response to be rendered before measuring another batch.
        if (Object.keys(rawArticles).some(index => articles[index] === undefined)) return;
        const nextStart = loaded.length
            ? Math.max(...loaded) + 1
            : currentArticleIndex + 1;

        if (nextStart >= flatToc.length) return;
        if (emptyRangeRef.current === nextStart) return;
        const request = {};
        nextRequestRef.current = request;
        try {
            const receivedCount = await fetchArticleRange(flatToc, nextStart, STEP_SIZE);
            // A missing/empty range must not cause an automatic request loop.
            if (receivedCount === 0 && nextRequestRef.current === request) {
                emptyRangeRef.current = nextStart;
            }
        } finally {
            if (nextRequestRef.current === request) nextRequestRef.current = null;
        }
    };

    const loadPrevArticle = async () => {
        if (isLoading || currentArticleIndex === null) return;
        const loaded = Object.keys(articles).map(Number);
        const prev = loaded.length ? Math.min(...loaded) - 1 : currentArticleIndex - 1;
        if (!flatToc[prev]) return;

        await fetchArticle(
            flatToc[prev]?._id,
            (article) => setRawArticles((a) => ({ ...a, [prev]: article.body || '' }))
        )
    };

    /* ------------------------------------------------------------------ */
    /*  Callback when an article enters the viewport                      */
    /* ------------------------------------------------------------------ */
    const handleArticleInView = (newIdx) => {
        setCurrentArticleIndex(newIdx);
        setSelectedArticleId(flatToc[newIdx]?._id);
        const node = flatToc[newIdx];
        setSelectedParentId(node.parent_id);
    };

    /* ------------------------------------------------------------------ */
    /*  Public — return every handler                                     */
    /* ------------------------------------------------------------------ */
    return {
        handleArticleSelect,
        loadNextArticle,
        loadPrevArticle,
        handleArticleInView,
    };
};
